import { apiRequest } from '../lib/api-client';
import { t } from '../lib/i18n';

/** Support tickets: the queue, one chamado, and the list of what a chamado can be about. */

export const TICKET_STATUSES = [
  'OPEN',
  'IN_PROGRESS',
  'PENDING_CUSTOMER',
  'ON_HOLD',
  'RESOLVED',
  'AWAITING_REVIEW',
  'CLOSED',
  'CANCELED',
] as const;

export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const TICKET_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;

export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

/**
 * The two halves a priority is computed from, and what kind of thing the chamado is (ITIL).
 *
 * The values are listed here for the dropdowns; the matrix that turns a pair of them into a
 * priority is fetched from the server rather than copied, so the screen can never offer a
 * combination that produces a different answer once saved.
 */
export const TICKET_URGENCIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;

export type TicketUrgency = (typeof TICKET_URGENCIES)[number];

export const TICKET_IMPACTS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;

export type TicketImpact = (typeof TICKET_IMPACTS)[number];

export const TICKET_KINDS = ['INCIDENT', 'REQUEST', 'PROBLEM', 'CHANGE'] as const;

export type TicketKind = (typeof TICKET_KINDS)[number];

export interface PriorityMatrix {
  urgencies: TicketUrgency[];
  impacts: TicketImpact[];
  kinds: TicketKind[];
  matrix: Record<TicketUrgency, Record<TicketImpact, TicketPriority>>;
  reviewWindowDays: number;
}

export function priorityMatrix(): Promise<PriorityMatrix> {
  return apiRequest('/v1/support/priority-matrix');
}

export type SlaHealth = 'MET' | 'RUNNING' | 'AT_RISK' | 'BREACHED' | 'PAUSED' | 'NONE';

export interface TicketSla {
  firstResponse: SlaHealth;
  resolution: SlaHealth;
  firstResponseDueAt: string | null;
  resolutionDueAt: string | null;
  minutesToFirstResponse: number | null;
  minutesToResolution: number | null;
}

export interface Ticket {
  id: string;
  number: number;
  subject: string;
  description: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  categoryId: string | null;
  contactId: string | null;
  companyId: string | null;
  requesterEmail: string | null;
  requesterName: string | null;
  queueId: string | null;
  assigneeId: string | null;
  source: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  satisfaction: number | null;
  urgency: TicketUrgency;
  impact: TicketImpact;
  kind: TicketKind;
  /** Somebody typed a priority; the matrix no longer overwrites it. */
  priorityLocked: boolean;
  reviewStartedAt: string | null;
  /** When the requester's silence cancels the chamado. */
  reviewDueAt: string | null;
  ownerId: string | null;
  createdAt: string;
  updatedAt: string;
  sla: TicketSla;
}

export interface TicketMessage {
  id: string;
  body: string;
  isPublic: boolean;
  authorType: 'AGENT' | 'CUSTOMER' | 'SYSTEM';
  authorId: string | null;
  createdAt: string;
}

export interface TicketEvent {
  id: string;
  eventType: string;
  fromStatus: TicketStatus | null;
  toStatus: TicketStatus | null;
  data: Record<string, unknown>;
  actorId: string | null;
  occurredAt: string;
}

export interface TicketDetail extends Ticket {
  messages: TicketMessage[];
  history: TicketEvent[];
  nextStatuses: TicketStatus[];
  /** Colleagues following the chamado without being the one answering it. */
  watcherIds: string[];
}

export interface TicketSummary {
  open: number;
  unassigned: number;
  pendingCustomer: number;
  awaitingReview: number;
  breached: number;
  resolvedToday: number;
  medianFirstResponseMinutes: number | null;
  satisfactionAverage: number | null;
}

export interface TicketQuery {
  status?: TicketStatus[];
  priority?: TicketPriority[];
  urgency?: TicketUrgency[];
  impact?: TicketImpact[];
  kind?: TicketKind[];
  watcherId?: string;
  assigneeId?: string;
  unassigned?: boolean;
  queueId?: string;
  categoryId?: string;
  search?: string;
  openOnly?: boolean;
  breachedOnly?: boolean;
  sort?: 'urgency' | 'newest' | 'oldest' | 'due';
  limit?: number;
  offset?: number;
}

function queryString(query: TicketQuery): string {
  const params = new URLSearchParams();

  for (const status of query.status ?? []) params.append('status', status);
  for (const priority of query.priority ?? []) params.append('priority', priority);
  for (const urgency of query.urgency ?? []) params.append('urgency', urgency);
  for (const impact of query.impact ?? []) params.append('impact', impact);
  for (const kind of query.kind ?? []) params.append('kind', kind);

  if (query.watcherId) params.set('watcherId', query.watcherId);
  if (query.assigneeId) params.set('assigneeId', query.assigneeId);
  if (query.unassigned) params.set('unassigned', 'true');
  if (query.queueId) params.set('queueId', query.queueId);
  if (query.categoryId) params.set('categoryId', query.categoryId);
  if (query.search) params.set('search', query.search);
  if (query.openOnly) params.set('openOnly', 'true');
  if (query.breachedOnly) params.set('breachedOnly', 'true');
  if (query.sort) params.set('sort', query.sort);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));

  return params.toString();
}

export function listTickets(query: TicketQuery): Promise<{ tickets: Ticket[]; total: number }> {
  return apiRequest(`/v1/support/tickets?${queryString(query)}`);
}

export function ticketSummary(): Promise<TicketSummary> {
  return apiRequest('/v1/support/tickets/summary');
}

export function getTicket(id: string): Promise<TicketDetail> {
  return apiRequest(`/v1/support/tickets/${id}`);
}

export interface NewTicket {
  subject: string;
  description?: string | null;
  priority?: TicketPriority;
  urgency?: TicketUrgency;
  impact?: TicketImpact;
  kind?: TicketKind;
  categoryId?: string | null;
  contactId?: string | null;
  companyId?: string | null;
  requesterEmail?: string | null;
  requesterName?: string | null;
  queueId?: string | null;
  assigneeId?: string | null;
  source?: string;
}

export function createTicket(payload: NewTicket): Promise<Ticket> {
  return apiRequest('/v1/support/tickets', { method: 'POST', body: payload });
}

export interface TicketPatch {
  subject?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  urgency?: TicketUrgency;
  impact?: TicketImpact;
  kind?: TicketKind;
  /** Sending a priority pins it; sending this false hands the chamado back to the matrix. */
  priorityLocked?: boolean;
  categoryId?: string | null;
  queueId?: string | null;
  assigneeId?: string | null;
  escalate?: boolean;
}

export function patchTicket(id: string, payload: TicketPatch): Promise<Ticket> {
  return apiRequest(`/v1/support/tickets/${id}`, { method: 'PATCH', body: payload });
}

export function replyToTicket(
  id: string,
  payload: { body: string; isPublic: boolean; status?: TicketStatus },
): Promise<TicketDetail> {
  return apiRequest(`/v1/support/tickets/${id}/replies`, { method: 'POST', body: payload });
}

/** Observers: somebody who needs to know what happens without being the one answering. */
export function watchTicket(id: string, userId: string): Promise<void> {
  return apiRequest(`/v1/support/tickets/${id}/watchers`, { method: 'POST', body: { userId } });
}

export function unwatchTicket(id: string, userId: string): Promise<void> {
  return apiRequest(`/v1/support/tickets/${id}/watchers/${userId}`, { method: 'DELETE' });
}

export interface TicketCategory {
  id: string;
  name: string;
  code: string;
  color: string | null;
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  position: number;
  active: boolean;
}

export function listCategories(): Promise<{ categories: TicketCategory[] }> {
  return apiRequest('/v1/support/categories');
}

/**
 * How an SLA reads on screen.
 *
 * `AT_RISK` and `BREACHED` are the only two that get a colour worth noticing. A queue where every
 * row is shouting is a queue where nothing is.
 */
export function slaTone(health: SlaHealth): 'neutral' | 'positive' | 'warning' | 'danger' | 'info' {
  if (health === 'BREACHED') return 'danger';
  if (health === 'AT_RISK') return 'warning';
  if (health === 'MET') return 'positive';
  if (health === 'PAUSED') return 'neutral';

  return 'info';
}

/**
 * The clock as somebody would say it: "em 42 min", "há 3 h".
 *
 * Past-due reads as elapsed rather than as a negative number, because "-180 min" is a figure
 * somebody has to translate before they can feel it.
 */
export function formatCountdown(minutes: number | null): string {
  if (minutes === null) return '—';

  const late = minutes < 0;
  const value = Math.abs(minutes);

  const said =
    value < 60
      ? `${value} min`
      : value < 60 * 24
        ? `${Math.round(value / 60)} h`
        : `${Math.round(value / (60 * 24))} d`;

  return t(late ? 'support.lateBy' : 'support.inTime').replace('{value}', said);
}

// ---------------------------------------------------------------------------
// The customer portal (Phase 47)
// ---------------------------------------------------------------------------

export interface PortalTicket {
  id: string;
  number: number;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  createdAt: string;
  updatedAt: string;
  /** Whether this chamado still takes a reply. */
  open: boolean;
  /** It is with the requester to accept, and this is when their silence cancels it. */
  awaitingReview: boolean;
  reviewDueAt: string | null;
  canRate: boolean;
  satisfaction: number | null;
}

export interface PortalThread extends PortalTicket {
  messages: { id: string; body: string; fromCustomer: boolean; createdAt: string }[];
}

export function listMyTickets(token: string): Promise<{ email: string; tickets: PortalTicket[] }> {
  return apiRequest(`/pub/tickets/${encodeURIComponent(token)}`);
}

export function readMyTicket(token: string, ticketId: string): Promise<PortalThread> {
  return apiRequest(`/pub/tickets/${encodeURIComponent(token)}/${ticketId}`);
}

export function replyToMyTicket(token: string, ticketId: string, body: string): Promise<void> {
  return apiRequest(`/pub/tickets/${encodeURIComponent(token)}/${ticketId}/reply`, {
    method: 'POST',
    body: { body },
  });
}

/** The requester's verdict, from their own link. Accepting closes the chamado. */
export function rateMyTicket(
  token: string,
  ticketId: string,
  rating: number,
  comment: string | null,
): Promise<void> {
  return apiRequest(`/pub/tickets/${encodeURIComponent(token)}/${ticketId}/rating`, {
    method: 'POST',
    body: { rating, comment },
  });
}

export interface PortalLink {
  id: string;
  email: string;
  expiresAt: string;
  url: string;
}

/** Issued by the team, for one address. The URL comes back exactly once. */
export function createPortalLink(input: {
  email: string;
  contactId?: string | null;
}): Promise<PortalLink> {
  return apiRequest('/v1/support/portal-links', { method: 'POST', body: input });
}
